import { useState } from "react";
import { useConsent } from "./consent/store";
import { extractCoords } from "./eventLocation";
import { captureEvent } from "./analytics";

// Interactive Google map for an event. The Google embed drops third-party
// cookies, so we tie it to the site's consent state instead of loading it
// unconditionally:
//   - visitor has accepted functional cookies (e.g. "Accept all") → auto-show
//   - otherwise → a "Show map" button; tapping it is itself consent for this
//     one load (handy for anyone who left the banner undecided or rejected).
//
// The pin is derived from the event itself so it always matches the WhatsApp
// share pin: we first look for a "lat, lng" pair anywhere in the event copy
// (our events carry a "GPS: 51.4..., -0.1..." line in the body), and fall back
// to the readable address if no coordinates are present.

type EventLike = {
  title: string;
  slug?: string | null;
  body?: string | null;
  meeting_point?: string | null;
  location_address?: string | null;
  lat?: number | null;
  lng?: number | null;
};

export default function EventMap({ event }: { event: EventLike }) {
  const { state } = useConsent();
  const [manualLoad, setManualLoad] = useState(false);

  const query =
    extractCoords(event) || event.location_address || event.meeting_point;
  if (!query) return null;

  const src = `https://www.google.com/maps?q=${encodeURIComponent(
    query,
  )}&z=16&output=embed`;

  const show = !!state?.categories.functional || manualLoad;

  return (
    <div className="mt-5">
      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
        Map
      </dt>
      <div
        className="relative rounded-2xl overflow-hidden border-2 border-ink/15 aspect-[16/9]"
        style={{ backgroundColor: "#FFF0E0" }}
      >
        {show ? (
          <iframe
            src={src}
            title={`Map showing ${event.title}`}
            className="absolute inset-0 w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              captureEvent("map_show", {
                event_slug: event.slug ?? "",
                event_title: event.title,
              });
              setManualLoad(true);
            }}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-2 text-center px-6 hover:bg-[#FFE6CC] transition-colors"
            aria-label="Show interactive Google map"
          >
            <span className="text-4xl" aria-hidden="true">
              📍
            </span>
            <span className="font-semibold text-ink">Show map</span>
            <span className="text-xs text-gray-500 max-w-[18rem] leading-relaxed">
              Loads an interactive Google map. Google may set cookies.
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
